import React from 'react';
import { cloneDeep, get, isNil } from 'lodash';

import { EXPERTISES } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/constants';
import { CorporealDocument } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import { ExpertiseTypes } from 'containers/CourtManagerLegalProceedings/JudgementAdd/Drawers/typings';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';

import AccountingExpertise from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/AccountingExpertise';
import MedicalExpertise from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/MedicalExpertise';
import MechanicalExpertise from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/MechanicalExpertise';
import {
  ExpertReportType,
  IVehiculeExpertise,
  IVictimExpertise,
} from 'containers/common/LegalProceedings/ExpertReport/typings';
import {
  VEHICULE_EXPERTISE_FIELDS,
  VICTIM_EXPERTISE_FIELDS,
} from 'containers/data-structure';
import { getDocumentId } from 'containers/common/LegalProceedings/ExpertReport/utils';
import { Listings } from 'containers/AppContainer/typings';

export interface FormsByExpertiseTypeProps {
  listings: Listings;
  expertiseId: string;
  expertiseJudgementAddDocumentId: string;
  victimExpertise: IVictimExpertise;
  vehiculeExpertise: IVehiculeExpertise;
  setExpertise: Function;
  documentIndex: number;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  documentId: string;
  setDocumentId: Function;
  readOnly?: boolean;
  isManager?: boolean;
  setIsDecisionDialgOpen?: Function;
  isDecisionDialgOpen?: boolean;
  validateVictimExpertise?: any;
  isRejected?: boolean;
  isAppeal?: boolean;
  isRecap?: boolean;
  isAssignedLawyer: boolean;
  isExpertReportValidated: boolean;
  username: string;
  isLawyerAssignedJudiciary?: boolean;
  expertReport: ExpertReportType;
  policyInfo: any;
  imagineDocuments: any;
  claimId: number;
  requestGetDocByDocumentumId: Function;
}

export const FormsByExpertiseType: React.FC<FormsByExpertiseTypeProps> = ({
  victimExpertise,
  vehiculeExpertise,
  expertiseJudgementAddDocumentId,
  listings,
  readOnly,
  expertiseId,
  uploadDocument,
  setDocumentId,
  uploadedDocument,
  resetUploadedDocument,
  isManager,
  setIsDecisionDialgOpen,
  isDecisionDialgOpen,
  documentIndex,
  validateVictimExpertise,
  isRejected,
  expertReport,
  ...props
}): JSX.Element => {
  const setVictimDocument = (document: CorporealDocument) => {
    const id = getDocumentId(
      String(victimExpertise.id),
      expertiseJudgementAddDocumentId as string,
    );

    const vicExpertise = cloneDeep(victimExpertise);

    if (id === props.documentId) {
      vicExpertise.victimExpertise[
        VICTIM_EXPERTISE_FIELDS.VICTIM_EXPERTISE_DOCUMENTS
      ].push(document);

      props.setExpertise(vicExpertise);
      resetUploadedDocument();
    }
  };

  const setVehiculeDocument = (document: CorporealDocument) => {
    const id = getDocumentId(
      String(vehiculeExpertise.id),
      expertiseJudgementAddDocumentId as string,
    );

    const vehExpertise = cloneDeep(vehiculeExpertise);

    if (id === props.documentId) {
      vehExpertise.vehiculeExpertise[
        VEHICULE_EXPERTISE_FIELDS.VEHICULE_EXPERTISE_DOCUMENTS
      ].push(document);

      props.setExpertise(vehExpertise);
      resetUploadedDocument();
    }
  };

  const renderByExpertiseId = () => {
    const commonProps = {
      listings,
      uploadDocument,
      uploadedDocument,
      resetUploadedDocument,
      setDocumentId,
      setIsDecisionDialgOpen,
      documentIndex,
      isDecisionDialgOpen,
      isManager,
    };
    switch (expertiseId as ExpertiseTypes) {
      case EXPERTISES.ACCOUNTING:
        return (
          <>
            <AccountingExpertise
              imagineDocuments={props.imagineDocuments}
              claimId={props.claimId}
              policyInfo={props.policyInfo}
              expertReport={vehiculeExpertise}
              setExpertReport={props.setExpertise}
              expertiseJudgementAddDocumentId={expertiseJudgementAddDocumentId}
              documentId={props.documentId}
              isRejected={isRejected}
              {...commonProps}
              isAppeal={props.isAppeal}
              isRecap={props.isRecap}
              isAssignedLawyer={props.isAssignedLawyer}
              isExpertReportValidated={props.isExpertReportValidated}
              username={props.username}
              isLawyerAssignedJudiciary={props.isLawyerAssignedJudiciary}
              expertReportState={expertReport}
              requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
            />
          </>
        );

      case EXPERTISES.MEDICAL:
        return (
          <MedicalExpertise
            victimExpertReport={victimExpertise as any}
            setVictimExpertReport={props.setExpertise}
            expertiseJudgementAddDocumentId={expertiseJudgementAddDocumentId}
            setDocument={setVictimDocument}
            validateVictimExpertise={validateVictimExpertise}
            readOnly={
              isManager || isRejected
                ? false
                : !isNil(
                    get(
                      victimExpertise.victimExpertise,
                      VICTIM_EXPERTISE_FIELDS.ID,
                    ),
                  )
            }
            {...commonProps}
            isRejected={isRejected}
            documentId={props.documentId}
            isAppeal={props.isAppeal}
            isRecap={props.isRecap}
            isAssignedLawyer={props.isAssignedLawyer}
            isExpertReportValidated={props.isExpertReportValidated}
            username={props.username}
            isLawyerAssignedJudiciary={props.isLawyerAssignedJudiciary}
            expertReportState={expertReport}
            imagineDocuments={props.imagineDocuments}
            claimId={props.claimId}
            policyInfo={props.policyInfo}
            requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
          />
        );

      case EXPERTISES.MECHANICAL:
        return (
          <MechanicalExpertise
            vehiculeExpertReport={vehiculeExpertise}
            readOnly={
              isManager || isRejected
                ? false
                : !isNil(
                    get(
                      vehiculeExpertise.vehiculeExpertise,
                      VEHICULE_EXPERTISE_FIELDS.ID,
                    ),
                  )
            }
            setVehiculeExpertReport={props.setExpertise}
            expertiseJudgementAddDocumentId={expertiseJudgementAddDocumentId}
            setDocument={setVehiculeDocument}
            {...commonProps}
            isRejected={isRejected}
            documentId={props.documentId}
            isAppeal={props.isAppeal}
            isRecap={props.isRecap}
            isAssignedLawyer={props.isAssignedLawyer}
            isExpertReportValidated={props.isExpertReportValidated}
            username={props.username}
            isLawyerAssignedJudiciary={props.isLawyerAssignedJudiciary}
            expertReportState={expertReport}
            imagineDocuments={props.imagineDocuments}
            claimId={props.claimId}
            policyInfo={props.policyInfo}
            requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
          />
        );
    }
  };

  return <>{renderByExpertiseId()}</>;
};

export default FormsByExpertiseType;
